import React from 'react';
import MultiSelectDropdown from '../MultiSelectDropdown';

interface Props {
  procuringOrganizations: string[];
  industries: string[];
  statuses: string[];
  selectedOrganizations: string[];
  selectedIndustries: string[];
  selectedStatuses: string[];
  hasOverride?: 'all' | 'with' | 'without';
  includeClosed: boolean;
  search: string;
  minDue?: string;
  maxDue?: string;
  onOrganizationChange: (values: string[]) => void;
  onIndustryChange: (values: string[]) => void;
  onStatusChange: (values: string[]) => void;
  onHasOverrideChange: (value: 'all' | 'with' | 'without') => void;
  onIncludeClosedChange: (value: boolean) => void;
  onSearchChange: (value: string) => void;
  onMinDueChange: (value: string) => void;
  onMaxDueChange: (value: string) => void;
  onReset: () => void;
  onDownloadCsv: () => void;
}

const IndustryOverrideFilters: React.FC<Props> = ({
  procuringOrganizations,
  industries,
  statuses,
  selectedOrganizations,
  selectedIndustries,
  selectedStatuses,
  hasOverride = 'all',
  includeClosed,
  search,
  minDue,
  maxDue,
  onOrganizationChange,
  onIndustryChange,
  onStatusChange,
  onHasOverrideChange,
  onIncludeClosedChange,
  onSearchChange,
  onMinDueChange,
  onMaxDueChange,
  onReset,
  onDownloadCsv,
}) => {
  return (
    <div className="space-y-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-700">Search</label>
          <input
            type="search"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search titles or descriptions..."
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
          />
        </div>

        <MultiSelectDropdown
          label="Procuring Organizations"
          placeholder="All organizations"
          options={procuringOrganizations}
          values={selectedOrganizations}
          onChange={onOrganizationChange}
        />

        <MultiSelectDropdown
          label="Industries"
          placeholder="All industries"
          options={industries}
          values={selectedIndustries}
          onChange={onIndustryChange}
        />

        <MultiSelectDropdown
          label="Statuses"
          placeholder="All statuses"
          options={statuses}
          values={selectedStatuses}
          onChange={onStatusChange}
        />

        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-700">Due Date</label>
          <div className="flex gap-3">
            <input
              type="date"
              value={minDue || ''}
              onChange={(e) => onMinDueChange(e.target.value)}
              className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
            />
            <input
              type="date"
              value={maxDue || ''}
              onChange={(e) => onMaxDueChange(e.target.value)}
              className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-100"
            />
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-700">Override Status</label>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input
                type="radio"
                name="override-filter"
                value="all"
                checked={hasOverride === 'all'}
                onChange={() => onHasOverrideChange('all')}
                className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              />
              All
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input
                type="radio"
                name="override-filter"
                value="with"
                checked={hasOverride === 'with'}
                onChange={() => onHasOverrideChange('with')}
                className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              />
              Overridden
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input
                type="radio"
                name="override-filter"
                value="without"
                checked={hasOverride === 'without'}
                onChange={() => onHasOverrideChange('without')}
                className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              />
              No override
            </label>
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-700">Include closed contracts</label>
          <div className="flex items-center gap-2">
            <input
              id="includeClosed"
              type="checkbox"
              checked={includeClosed}
              onChange={(e) => onIncludeClosedChange(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="includeClosed" className="text-sm text-slate-600">
              Show closed/archived contracts
            </label>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          onClick={onDownloadCsv}
          className="inline-flex items-center rounded-full border border-blue-500 px-4 py-1.5 text-sm font-semibold text-blue-600 transition hover:bg-blue-50"
        >
          Download CSV
        </button>
        <button
          type="button"
          onClick={onReset}
          className="inline-flex items-center rounded-full border border-slate-200 px-4 py-1.5 text-sm font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-700"
        >
          Reset filters
        </button>
      </div>
    </div>
  );
};

export default IndustryOverrideFilters;
